import React from 'react';
import { Accordion, AccordionItem, ContentLoader } from '@forge/common';
import { useTranslation } from 'react-i18next';
import ActivityFilterCheckboxGroup, { CheckboxGroupProps } from './ActivityFilterCheckboxGroup';
import ActivityErrorState from '../shared/ActivityErrorState';

export interface FilterSection extends CheckboxGroupProps {
  id: string;
  title: string;
  enableInfiniteScroll?: boolean;
}

export interface ActivityFilterBodyProps {
  isLoading: boolean;
  error: string | null;
  onRefresh: () => void;
  sections: FilterSection[];
}

const ActivityFilterBody: React.FC<ActivityFilterBodyProps> = React.memo(({ isLoading, error, onRefresh, sections }) => {
  const { t } = useTranslation();
  if (isLoading) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center" data-testid="filter-loading">
        <ContentLoader>
          {t('activityLogLoading')}
        </ContentLoader>
      </div>
    );
  }

  if (error) {
    return (
      <ActivityErrorState
        onRefresh={onRefresh}
        message={t('activityLogFilterError')}
        className="tw-flex tw-flex-col tw-items-center tw-gap-4 tw-py-10"
      />
    );
  }

  return (
    <div className="tw-px-6">
      <Accordion id="activity-filter-accordion" className="activity-filter-accordion" defaultOpenIndex={[0]}>
        {sections.map(({ id, title, groupId, options, selected, onToggle, onSelectAll, enableInfiniteScroll }) => (
          <AccordionItem
            key={id}
            id={id}
            className="activity-filter-accordion-item"
            title={title}
            titleCounter={selected.length > 0 ? <span className="activity-filter-badge">{selected.length}</span> : undefined}
            fullWidth
          >
            <ActivityFilterCheckboxGroup
              groupId={groupId}
              options={options}
              selected={selected}
              onToggle={onToggle}
              onSelectAll={onSelectAll}
              enableInfiniteScroll={enableInfiniteScroll}
            />
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
});

export default ActivityFilterBody;
